import { useState } from 'react';
import {
  Card,
  Title,
  Stack,
  TextInput,
  Textarea,
  Button,
  Alert,
  Group,
  Select,
} from '@mantine/core';
import { IconShoppingBag, IconCircleCheck, IconAlertTriangle } from '@tabler/icons-react';
import { submitTakeoutRequest } from '../api-client';

const PICKUP_HOURS = [
  { value: '11', label: '11:00' },
  { value: '12', label: '12:00' },
  { value: '13', label: '13:00' },
  { value: '14', label: '14:00' },
  { value: '15', label: '15:00' },
  { value: '16', label: '16:00' },
  { value: '17', label: '17:00' },
  { value: '18', label: '18:00' },
  { value: '19', label: '19:00' },
  { value: '20', label: '20:00' },
];

function hourToFutureIso(hour: number): string {
  const d = new Date();
  d.setHours(hour, 0, 0, 0);
  if (d.getTime() <= Date.now()) {
    d.setDate(d.getDate() + 1);
  }
  return d.toISOString();
}

export function TakeoutForm({ venueId }: { venueId: string }) {
  const [customerRef, setCustomerRef] = useState('');
  const [pickupHour, setPickupHour] = useState('18');
  const [itemNote, setItemNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ requestId: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = customerRef.trim().length > 0 && itemNote.trim().length > 0;

  const onSubmit = async (): Promise<void> => {
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const r = await submitTakeoutRequest({
        venueId,
        customerRef: customerRef.trim(),
        pickupTime: hourToFutureIso(Number(pickupHour)),
        itemNote: itemNote.trim(),
      });
      setResult({ requestId: r.requestId });
      setCustomerRef('');
      setItemNote('');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        <Group gap="xs">
          <IconShoppingBag size={20} />
          <Title order={3} size="h4">
            外帶預訂
          </Title>
        </Group>

        {result && (
          <Alert
            color="green"
            icon={<IconCircleCheck size={16} />}
            title="預訂成功"
            withCloseButton
            onClose={() => setResult(null)}
          >
            單號：{result.requestId.slice(0, 8)}... 請於取餐時間前到店
          </Alert>
        )}

        {error && (
          <Alert
            color="red"
            icon={<IconAlertTriangle size={16} />}
            title="送出失敗"
            withCloseButton
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <TextInput
          label="姓名 / 聯絡方式"
          placeholder="例：王小明 0912-345-678"
          value={customerRef}
          onChange={(e) => setCustomerRef(e.currentTarget.value)}
          required
        />

        <Select
          label="取餐時間"
          description="若所選時段已過，將自動改為明日"
          data={PICKUP_HOURS}
          value={pickupHour}
          onChange={(v) => setPickupHour(v ?? '18')}
          required
        />

        <Textarea
          label="餐點備註"
          placeholder="例：蛋糕一份 不加糖、司康兩個"
          value={itemNote}
          onChange={(e) => setItemNote(e.currentTarget.value)}
          minRows={2}
          autosize
          required
        />

        <Button
          onClick={() => void onSubmit()}
          loading={submitting}
          disabled={!canSubmit}
          fullWidth
        >
          送出預訂
        </Button>
      </Stack>
    </Card>
  );
}
